import type { ReactNode } from 'react';
import type { FamilyDraft } from '../api/coach-clients.js';

/**
 * The family's details as one form (mockup *Clients and Texted Links* v8):
 * the coach's Add a family and Edit details (athletes first, then the parent,
 * as Book's Add a new athlete), and the parent's own "Complete your coaching
 * profile" (the parent first). Labels are the app's own - "First name",
 * "Last name", "Phone", "Email" - under ATHLETE and PARENT.
 *
 * Add another athlete adds a set of name fields, and waits until the athlete
 * above has a first and last name (asked 22 October); one added here can be
 * taken out again with Remove. An athlete already on the family has no Remove: they
 * may have lessons.
 *
 * `problem` puts the API's refusal under the input it is about.
 *
 * SUGGESTIONS (ruled 22 October): on in the parent's own page (`autofill`) -
 * the parent's boxes as their own name, phone and email, each athlete's as a
 * separate person, so filling one never fills the other. Off on the coach's
 * forms, where the details are another family's and the phone would offer the
 * coach's own.
 */

export const EMPTY_FAMILY: FamilyDraft = {
  parent_first_name: '',
  parent_last_name: '',
  parent_phone: '',
  parent_email: '',
  athletes: [],
};

export function FamilyForm({
  value,
  onChange,
  order,
  canAddAthletes,
  problem,
  idPrefix,
  autofill = false,
  afterAthlete,
}: {
  value: FamilyDraft;
  onChange: (next: FamilyDraft) => void;
  order: 'athletes-first' | 'parent-first';
  canAddAthletes: boolean;
  problem: { field: string; message: string } | null;
  idPrefix: string;
  autofill?: boolean;
  /** Under each athlete: the parent's own page puts their bookings here. */
  afterAthlete?: (athleteId: string | undefined) => ReactNode;
}) {
  const set = (key: keyof Omit<FamilyDraft, 'athletes'>) => (next: string) => onChange({ ...value, [key]: next });
  const setAthlete = (index: number, key: 'first_name' | 'last_name' | 'date_of_birth', next: string) =>
    onChange({
      ...value,
      athletes: value.athletes.map((athlete, at) => (at === index ? { ...athlete, [key]: next } : athlete)),
    });
  const hint = (field: string) => (problem?.field === field ? problem.message : null);
  const fill = (token: string) => (autofill ? token : 'off');
  const last = value.athletes[value.athletes.length - 1];
  const lastNamed = !last || (last.first_name.trim() !== '' && last.last_name.trim() !== '');

  const athletes = (
    <>
      {value.athletes.map((athlete, index) => (
        <div className="family-athlete" key={athlete.id ?? `new-${index}`}>
          <div className="family-athlete-head">
            <span className="section-label">Athlete</span>
            {!athlete.id ? (
              <button
                type="button"
                className="link-button"
                onClick={() => onChange({ ...value, athletes: value.athletes.filter((_, at) => at !== index) })}
              >
                Remove
              </button>
            ) : null}
          </div>
          <Field
            id={`${idPrefix}-athlete-${index}-first`}
            label="First name"
            value={athlete.first_name}
            onChange={(next) => setAthlete(index, 'first_name', next)}
            hint={hint(`athletes.${index}.first_name`)}
            autoComplete={fill(`section-athlete-${index} given-name`)}
          />
          <Field
            id={`${idPrefix}-athlete-${index}-last`}
            label="Last name"
            value={athlete.last_name}
            onChange={(next) => setAthlete(index, 'last_name', next)}
            hint={hint(`athletes.${index}.last_name`)}
            autoComplete={fill(`section-athlete-${index} family-name`)}
          />
          {athlete.needs_birthday ? (
            <Field
              id={`${idPrefix}-athlete-${index}-birthday`}
              label="Date of birth"
              type="date"
              value={athlete.date_of_birth ?? ''}
              onChange={(next) => setAthlete(index, 'date_of_birth', next)}
              hint={hint(`athletes.${index}.date_of_birth`)}
              autoComplete="off"
            />
          ) : null}
          {afterAthlete?.(athlete.id)}
        </div>
      ))}
      {canAddAthletes ? (
        <button
          type="button"
          className="secondary"
          disabled={!lastNamed}
          onClick={() => onChange({ ...value, athletes: [...value.athletes, { first_name: '', last_name: '' }] })}
        >
          {value.athletes.length === 0 ? 'Add an athlete' : 'Add another athlete'}
        </button>
      ) : null}
    </>
  );

  const parentBirthday = value.parent_needs_birthday ? (
    <Field
      id={`${idPrefix}-parent-birthday`}
      label="Date of birth"
      type="date"
      value={value.parent_date_of_birth ?? ''}
      onChange={(next) => onChange({ ...value, parent_date_of_birth: next })}
      hint={hint('parent_date_of_birth')}
      autoComplete={fill('bday')}
    />
  ) : null;

  const parent = (
    <>
      <span className="section-label">Adult</span>
      <Field id={`${idPrefix}-parent-first`} label="First name" value={value.parent_first_name} onChange={set('parent_first_name')} hint={hint('parent_first_name')} autoComplete={fill('given-name')} />
      <Field id={`${idPrefix}-parent-last`} label="Last name" value={value.parent_last_name} onChange={set('parent_last_name')} hint={hint('parent_last_name')} autoComplete={fill('family-name')} />
      <Field id={`${idPrefix}-parent-phone`} label="Phone" type="tel" value={value.parent_phone} onChange={set('parent_phone')} hint={hint('parent_phone')} autoComplete={fill('tel')} />
      <Field id={`${idPrefix}-parent-email`} label="Email" type="email" value={value.parent_email} onChange={set('parent_email')} hint={hint('parent_email')} autoComplete={fill('email')} />
      {parentBirthday}
    </>
  );

  return order === 'athletes-first' ? (
    <>
      {athletes}
      {parent}
    </>
  ) : (
    <>
      {parent}
      {athletes}
    </>
  );
}

/** Every box filled that must be; the email, when given or required, looks like one. */
export function familyReady(value: FamilyDraft, emailRequired: boolean): boolean {
  const filled = (text: string) => text.trim() !== '';
  const email = value.parent_email.trim();
  return (
    filled(value.parent_first_name) &&
    filled(value.parent_last_name) &&
    value.athletes.every((athlete) => filled(athlete.first_name) && filled(athlete.last_name)) &&
    (email === '' ? !emailRequired : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  hint,
  autoComplete,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  hint: string | null;
  autoComplete: string;
  type?: string;
}) {
  return (
    <div className="field" {...(hint ? { 'data-invalid': true } : {})}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        autoCapitalize={type === 'email' ? 'none' : 'words'}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint ? (
        <span className="hint" role="alert">
          {hint}
        </span>
      ) : null}
    </div>
  );
}
